import { useCallback, useEffect, useRef, useState } from 'react';
import { push, replace } from 'redux-first-router';
import type { IntersectionPayload } from '@/hooks/useIntersectionListener';
import { CompanyReportSection, useSection } from '../useSection';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import useManualScrollRestore from './useManualScrollRestore';
import usePopstateHandler from './usePopstateHandler';
import type { ANCHOR_TO_SECTION_MAPPING } from '@/constants/anchors';
import { useMount } from '@simplywallst/ui-core';

export type UpdateMethod = 'push' | 'replace' | 'none';
export type CompanySubSection =
  | CompanyReportSection
  | keyof typeof ANCHOR_TO_SECTION_MAPPING;

//This hook handles the url logic for the company split component
function useReportUrlHandler() {
  const [showFullReport, setShowFullReport] = useState(false);
  const [updateMethod, setUpdateMethod] = useState<UpdateMethod>('replace');
  const canonicalUrl = useCompanyCanonicalUrl();
  const { currentSection, initialSection } = useSection();

  useMount(() => {
    if (window.location.hash === '') {
      window.scrollTo(0, 0);
    }
  });

  const onIntersectionPathUpdate = useCallback(
    (data: IntersectionPayload, shareToken: string, section?: string) => {
      if (canonicalUrl === '' || !section) return;

      //We want to only call this function if sections changed
      if (section === currentSection) return;

      //Ignore intersection updates where section in view is not at least 20%
      //This is to stop section selection from flickering
      //Except for summary section as header may be mostly shown
      const coverage = data.entities[section]?.coverage;
      if (section !== 'summary' && coverage < 0.2) return;

      const newUrl = `${canonicalUrl}${
        section === 'summary' ? '' : `/${section}`
      }${shareToken !== '' ? `?token=${shareToken}` : ''}`;
      const updateUrl =
        updateMethod === 'push'
          ? push
          : updateMethod === 'replace'
          ? replace
          : null;

      updateUrl?.(newUrl);
      setUpdateMethod('replace');
    },
    [canonicalUrl, currentSection, updateMethod]
  );

  const goToSection = useCallback(
    (section: CompanySubSection, method: UpdateMethod = 'none') => {
      setUpdateMethod(method);
      const sectionEl = document.getElementById(section);
      sectionEl?.scrollIntoView();
    },
    []
  );

  const showFullReportAt = useCallback(
    (section?: CompanySubSection, method: UpdateMethod = 'none') => {
      if (!section) {
        setShowFullReport(true);
      } else if (section === 'summary') {
        setShowFullReport(true);
        window.scrollTo(0, 0);
      } else if (showFullReport) {
        goToSection(section, method);
      } else {
        setShowFullReport(true);
        setUpdateMethod('none');
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => goToSection(section, method), {
            timeout: 800,
          });
        } else {
          setTimeout(() => goToSection(section, method), 800);
        }
      }
    },
    [goToSection, showFullReport, setShowFullReport]
  );

  //For the back / forward buttons to jump to the right section
  //we need to set history.scrollRestoration = 'manual'
  //otherwise it jumps back to the previously saved scroll position
  useManualScrollRestore();
  //A ref is needed here as using currentSection as a dependency to the useEffect below
  //unregisters the listener before it has a chance to fire
  const sectionRef = useRef<CompanyReportSection>(initialSection);
  useEffect(() => {
    sectionRef.current = currentSection;
  }, [currentSection]);
  usePopstateHandler(
    useCallback(() => {
      goToSection(sectionRef.current);
    }, [goToSection])
  );

  return {
    onIntersectionPathUpdate,
    goToSection,
    showFullReportAt,
    showFullReport,
  };
}
export default useReportUrlHandler;
